import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Emails come from Cognito on one side and DynamoDB on the other, so compare
// them case- and whitespace-insensitively rather than with ===.
export function sameEmail(a, b) {
  return Boolean(a) && Boolean(b) && a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function formatBytes(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function formatDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

export function formatDateShort(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));
}

export function rollbackRatePercent(rate) {
  return (rate * 100).toFixed(1) + "%";
}

/**
 * Render a target-country list. Entries arrive either as plain codes or as
 * { name, code } objects, depending on the endpoint.
 *
 * `emptyValue` differs by call site: the tables and PDF want an em dash, the
 * summary rows want null so the row is skipped entirely.
 */
export function formatTargetCountries(arr, emptyValue = "—") {
  if (!Array.isArray(arr) || !arr.length) {
    return emptyValue;
  }
  return arr.map((c) => (typeof c === "object" ? `${c.name} (${c.code})` : c)).join(", ");
}

/**
 * The service's own refusal message, which names what is wrong and what to do.
 *
 * On an axios error `err.message` is only "Request failed with status code 409";
 * on a fetch-based helper it is the hand-built "PATCH /x → 409: {...}" string.
 * Reading either instead of the response body throws away the part the operator
 * actually needs, and they are left with a failure that explains nothing. The
 * services deliberately put a usable sentence in `message`, for example
 * "This firmware is not approved for US. Approved: in, india."
 *
 * Handles both shapes: axios puts the parsed body on `err.response.data`, while
 * several helpers in lib/api.js throw a plain Error whose text ends in the raw
 * JSON body - that tail is parsed here rather than shown as-is.
 */
export function serviceMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data?.message) return data.message;

  const text = err?.message || "";
  const brace = text.indexOf("{");
  if (brace !== -1) {
    try {
      const parsed = JSON.parse(text.slice(brace));
      if (parsed?.message) return parsed.message;
    } catch {
      /* not JSON - fall through to the raw text */
    }
  }
  return text || fallback;
}

/**
 * The human label for a Cognito role.
 *
 * One definition, because this is shown in places a reviewer reads as a record
 * of WHO did something: the deprecation dialog's audit-log preview and the
 * upload wizard's attestation line. BaselinesPage previously carried its own
 * copy that mapped BOTH FleetAdmin and ReleaseManager to "Fleet Admin", so a
 * Release Manager was shown - and would have attested - under a role they do
 * not hold, in a preview of a 21 CFR Part 11 record.
 *
 * An unknown role is returned as-is rather than blanked: a label nobody
 * recognises is better than a field that silently empties.
 */
const ROLE_LABELS = {
  FleetAdmin: "Fleet Admin",
  ReleaseManager: "Release Manager",
  SupportEngineer: "Support Engineer",
  Nurse: "Nurse",
  Patient: "Patient",
};

export function roleLabel(role) {
  return ROLE_LABELS[role] || role || "";
}
